import {
	Box,
	Button,
	Chip,
	Divider,
	LinearProgress,
	Paper,
	Stack,
	Tooltip,
	Typography,
	Autocomplete,
	TextField,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PageTitle from "../../components/PageTitle/PageTitle";
import "./Analytics.css";

import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import AnalyticsCharts from "../../components/AnalyticsCharts/AnalyticsCharts";
import DateRange from "../../components/DateRange/DateRange";
// import FilterBox from "../../components/FilterBox/FilterBox";
import AnalyticsTables from "../../components/AnalyticsTables/AnalyticsTables";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useTranslation } from "react-i18next";
import { getAllUsers } from "../../services/analyticsService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getUserAllocations } from "../../services/manageSitesService";
import { getProvisionedMachines } from "../../services/manageSiteMachinesService";
import { useOptions } from "../../context/optionContext";
import { resolveEnabledAdminModules } from "../../utilities/adminModules";
import { canAccessOrderAnalytics } from "../../utilities/pageAccess";
// import { DateRangePicker } from "react-date-range";
import { alpha } from "@mui/system";
const getCurrentDate = () => new Date();
const formatDate = (date) => date.toLocaleDateString();
const formatApiDate = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
		return "";
	}

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

// Get the start of the current week (Monday)
const getStartOfWeek = (date) => {
	const day = date.getDay(),
		diff = date.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
	date.setDate(diff);
	date.setHours(0, 0, 0, 0);
	return date;
};

// Get the end of the current week (Sunday)
const getEndOfWeek = (date) => {
	const startOfWeek = getStartOfWeek(date);
	startOfWeek.setDate(startOfWeek.getDate() + 6);
	startOfWeek.setHours(23, 59, 59, 999);
	return startOfWeek;
};

// Get the start and end of the current month
const getStartOfMonth = (date) => {
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfMonth = (date) => {
	date.setMonth(date.getMonth() + 1);
	date.setDate(0);
	date.setHours(23, 59, 59, 999);
	return date;
};

// Get the start and end of the current quarter
const getStartOfQuarter = (date) => {
	const currentMonth = date.getMonth();
	let startMonth;
	if (currentMonth <= 2) startMonth = 0; // January to March (Q1)
	else if (currentMonth <= 5) startMonth = 3; // April to June (Q2)
	else if (currentMonth <= 8) startMonth = 6; // July to September (Q3)
	else startMonth = 9; // October to December (Q4)

	date.setMonth(startMonth);
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfQuarter = (date) => {
	const currentMonth = date.getMonth();
	let endMonth;
	if (currentMonth <= 2) endMonth = 2; // Q1
	else if (currentMonth <= 5) endMonth = 5; // Q2
	else if (currentMonth <= 8) endMonth = 8; // Q3
	else endMonth = 11; // Q4

	date.setMonth(endMonth + 1);
	date.setDate(0);
	date.setHours(23, 59, 59, 999);
	return date;
};

// Get the start and end of the current year
const getStartOfYear = (date) => {
	date.setMonth(0);
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfYear = (date) => {
	date.setMonth(11);
	date.setDate(31);
	date.setHours(23, 59, 59, 999);
	return date;
};

const Analytics = () => {
	const [error, setError] = useState(null);
	const [customers, setCustomers] = useState([]);
	const [customersOptions, setCustomersOptions] = useState([]);
	const [machines, setMachines] = useState([]);
	const [machinesOptions, setMachinesOptions] = useState([]);
	const [additionalFilters, setAdditionalFilters] = useState([]);
	const [sites, setSites] = useState([]);
	const [sitesOptions, setSitesOptions] = useState([]);
	const [users, setUsers] = useState([]);
	const [usersOptions, setUsersOptions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [buttonShow, setButtonShow] = useState(false);
	const [selectedRange, setSelectedRange] = useState({
		key: "W",
		label: "Week",
		startDate: formatDate(getStartOfWeek(new Date())),
		endDate: formatDate(getEndOfWeek(new Date())),
	});
	const [selectedDateRange, setSelectedDateRange] = useState([
		getStartOfWeek(new Date()),
		getEndOfWeek(new Date()),
	]);
	const { user, hasPermission } = useAuth();
	const { selectedSite, selectedSiteData, selectedSiteKey } = useSite();
	const { t } = useTranslation();
	const { optionKeys } = useOptions();
	const enabledAdminModuleSet = useMemo(() => new Set(resolveEnabledAdminModules(optionKeys)), [optionKeys]);
	const ALL_VALUE = t('MENU_ITEMS.ALL');
	const [filters, setFilters] = useState({
		customer: ALL_VALUE,
		site: ALL_VALUE,
		user: ALL_VALUE,
		region: t('MENU_ITEMS.ALL'),
		machine: ALL_VALUE,
	});

	const buttons = [
		{ key: "T", label: t('DATE_RANGES.TODAY') },
		{ key: "W", label: t('DATE_RANGES.WEEK') },
		{ key: "M", label: t('DATE_RANGES.MONTH') },
		{ key: "Q", label: t('DATE_RANGES.QUARTER') },
		{ key: "Y", label: t('DATE_RANGES.YEAR') },
		// { key: "All", label: "All" },
	];


	const [chartQueryFilter, setChartQueryFilter] = useState({});
	const [showCharts, setShowCharts] = useState(true);
	const [selectedMetric, setSelectedMetric] = useState(null);
	const [accessScope, setAccessScope] = useState({ allowAll: false, allowNone: false });
	const [allocatedSiteIds, setAllocatedSiteIds] = useState([]);
	const [allocatedCustomerIds, setAllocatedCustomerIds] = useState([]);
	const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
	const [initialSiteFilterState, setInitialSiteFilterState] = useState(() => ({
		siteNumber: selectedSite || "",
		siteKey: selectedSiteKey || "",
		ready: !selectedSite,
	}));
	const appliedSelectedSiteRef = useRef(false);

	// Autocomplete search input + debounce
	const [customerInput, setCustomerInput] = useState("");
	const [siteInput, setSiteInput] = useState("");
	const [userInput, setUserInput] = useState("");
	const [machineInput, setMachineInput] = useState("");
	const [debouncedCustomerInput, setDebouncedCustomerInput] = useState("");
	const [debouncedSiteInput, setDebouncedSiteInput] = useState("");
	const [debouncedUserInput, setDebouncedUserInput] = useState("");
	const [debouncedMachineInput, setDebouncedMachineInput] = useState("");

	useEffect(() => { const t = setTimeout(() => setDebouncedCustomerInput(customerInput), 200); return () => clearTimeout(t); }, [customerInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedSiteInput(siteInput), 200); return () => clearTimeout(t); }, [siteInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedUserInput(userInput), 200); return () => clearTimeout(t); }, [userInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedMachineInput(machineInput), 200); return () => clearTimeout(t); }, [machineInput]);

	useEffect(() => {
		appliedSelectedSiteRef.current = false;
		if (selectedSite) setLoading(true);
		setInitialSiteFilterState({
			siteNumber: selectedSite || "",
			siteKey: selectedSiteKey || "",
			ready: !selectedSite,
		});
	}, [selectedSite, selectedSiteKey, selectedSiteData?.site_id, selectedSiteData?.cus_id]);

	const normalizeMachineNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const normalizeEmail = (value) => {
		if (value == null) return "";
		return String(value).trim().toLowerCase();
	};
	const normalizeSiteNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const normalizeCustomerNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const toPositiveNumber = (value) => {
		const number = Number(value);
		return Number.isFinite(number) && number > 0 ? number : null;
	};
	const getSiteId = (site) =>
		toPositiveNumber(site?.site_id ?? site?.customer_site_id ?? site?.id);
	const getSiteCustomerId = (site) =>
		toPositiveNumber(site?.cus_id ?? site?.cust_id ?? site?.customer_id ?? site?.customer?.cus_id);
	const getSiteSelectionValue = (site) => {
		const siteId = getSiteId(site);
		const customerId = getSiteCustomerId(site);
		if (!siteId) return "";
		return customerId ? `${siteId}:${customerId}` : String(siteId);
	};
	const getFilterSiteId = (value) => toPositiveNumber(String(value || "").split(":")[0]);
	const getFilterSiteCustomerId = (value) => {
		const [, customerId] = String(value || "").split(":");
		return toPositiveNumber(customerId);
	};
	const uniqueBy = (list, keyFn) => {
		const map = new Map();
		(list || []).forEach((item) => {
			const key = keyFn(item);
			if (!key || map.has(key)) return;
			map.set(key, item);
		});
		return Array.from(map.values());
	};
	const isAllValue = (value) => value == null || value === ALL_VALUE;
	const allOption = useMemo(() => {
		const label = t("MENU_ITEMS.ALL");
		return { value: ALL_VALUE, label, searchText: label.toLowerCase() };
	}, [t]);
	const withAllOption = useCallback(
		(options = []) => {
			const filtered = options.filter((opt) => opt?.value !== ALL_VALUE);
			return [allOption, ...filtered];
		},
		[allOption]
	);

	// Efficient filtering for large option lists (debounced, capped)
	const cap = 500;
	const getSearchQuery = (inputValue, selectedOption) => {
		const query = (inputValue || "").toLowerCase();
		if (!query) return "";
		const selectedLabel = (selectedOption?.label || "").toLowerCase();
		return query === selectedLabel ? "" : query;
	};

	const selectedCustomerOption = customersOptions.find(o => o.value === filters.customer);
	const selectedSiteOption = sitesOptions.find(o => o.value === filters.site);
	const selectedUserOption = usersOptions.find(o => o.value === filters.user);
	const selectedMachineOption = machinesOptions.find(o => o.value === filters.machine);
	const displayedCustomerOptions = useMemo(() => {
		const base = customersOptions || [];
		const q = getSearchQuery(debouncedCustomerInput, selectedCustomerOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [customersOptions, debouncedCustomerInput, selectedCustomerOption]);

	const displayedSiteOptions = useMemo(() => {
		const base = sitesOptions || [];
		const q = getSearchQuery(debouncedSiteInput, selectedSiteOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [sitesOptions, debouncedSiteInput, selectedSiteOption]);

	const displayedUserOptions = useMemo(() => {
		const base = usersOptions || [];
		const q = getSearchQuery(debouncedUserInput, selectedUserOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [usersOptions, debouncedUserInput, selectedUserOption]);

	const displayedMachineOptions = useMemo(() => {
		const base = machinesOptions || [];
		const q = getSearchQuery(debouncedMachineInput, selectedMachineOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		const deduped = [];
		const seen = new Set();
		filtered.forEach((opt) => {
			const key =
				normalizeMachineNumber(opt?.machine_number) ||
				normalizeMachineNumber(opt?.label) ||
				String(opt?.value);
			if (!key || seen.has(key)) return;
			seen.add(key);
			deduped.push(opt);
		});
		return deduped.slice(0, cap);
	}, [machinesOptions, debouncedMachineInput, selectedMachineOption]);

	const isAdminUser = useMemo(() => {
		if (!user) return false;
		if (user.user_id === 1) return true;
		const roleToken =
			user?.role?.role_type ||
			user?.role?.role_name ||
			user?.role?.role ||
			user?.user_role_name ||
			user?.user_role ||
			"";
		return String(roleToken).trim().toLowerCase() === "admin";
	}, [user]);

	useEffect(() => {
		const fetchUsers = async (scope) => {
			try {
				if (scope.allowNone) {
					setUsers([]);
					return;
				}
				const response = await getAllUsers();
				if (response.data.length > 0) {
					const dedupedUsers = uniqueBy(response.data, (u) => normalizeEmail(u?.user_email) || String(u?.user_id));
					setUsers(dedupedUsers);
				} else {
					setError(`Failed to fetch users: No users found`);
				}
			} catch (err) {
				setError(`Failed to fetch users from: ${err.message}`);
			}
		};

		const fetchCustomers = async (scope) => {
			try {
				if (scope.allowNone) {
					setCustomers([]);
					return;
				}
				const response = await getCustomersCusSites();
				if (response.status) {
					const allCustomers = response.data.filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					const dedupedCustomers = uniqueBy(allCustomers, (c) =>
						normalizeCustomerNumber(c?.customer_number) || String(c?.cus_id)
					);
					setCustomers(dedupedCustomers);
				} else {
					setError("Failed to fetch customers.");
				}
			} catch (err) {
				setError("Failed to fetch customers: " + err.message);
			}
		};
		const fetchSites = async (siteIds, customerIds, scope) => {
			try {
				if (scope.allowNone) {
					setSites([]);
					return;
				}
				const response = await getAllCustomerSites();
				if (response.status) {
					const allSites = response.data.filter(
						(customer) => customer.customer_site_name && customer.customer_site_name !== "EMPTY"
					);
					const dedupedSites = uniqueBy(allSites, (s) =>
						`${getSiteId(s) || normalizeSiteNumber(s?.customer_site_number || s?.site_number)}:${getSiteCustomerId(s) || ""}`
					);
					let filteredSites = [];
					if (scope.allowAll) {
						filteredSites = dedupedSites;
					} else if (siteIds.length > 0) {
						filteredSites = dedupedSites.filter((site) => siteIds.includes(getSiteId(site)));
					} else if (customerIds.length > 0) {
						filteredSites = dedupedSites.filter((site) => customerIds.includes(getSiteCustomerId(site)));
					}
					setSites(filteredSites);
				} else {
					setError("Failed to fetch customers.");
				}
			} catch (err) {
				setError("Failed to fetch customers: " + err.message);
			}
		};

		const fetchOrders = async () => { };

		const fetchMachines = async (scope) => {
			try {
				if (scope.allowNone) {
					setMachines([]);
					return;
				}

				const response = await getProvisionedMachines({ includeInactive: true });
				if (response.status) {
					setMachines(response.data || []);
				} else {
					setError("Failed to fetch machines.");
				}
			} catch (error) {
				console.error("Error fetching machines:", error);
				setError("Failed to fetch machines.");
			}
		};

		const init = async () => {
			if (!user) return;
			setFilterOptionsLoaded(false);
			setLoading(true);
			try {
				const uid = user.user_id;
				const allocResp = await getUserAllocations(uid);
				const allocations = allocResp?.data?.allocations || [];
				const siteIds = [...new Set(allocations.map((a) => Number(a.site_id)).filter(Boolean))];
				const cusIds = [...new Set(allocations.map((a) => Number(a.cus_id)).filter(Boolean))];
				const hasAllocations = siteIds.length > 0 || cusIds.length > 0;
				const scope = {
					allowAll: !hasAllocations,
					allowNone: false,
				};
				setAccessScope(scope);
				setAllocatedSiteIds(siteIds);
				setAllocatedCustomerIds(cusIds);
				await fetchSites(siteIds, cusIds, scope);
				await fetchUsers(scope);
				await fetchCustomers(scope);
				await fetchMachines(scope);
			} catch (e) {
				const scope = {
					allowAll: Boolean(isAdminUser),
					allowNone: !isAdminUser,
				};
				setAccessScope(scope);
				setAllocatedSiteIds([]);
				setAllocatedCustomerIds([]);
				await fetchSites([], [], scope);
				await fetchUsers(scope);
				await fetchCustomers(scope);
				await fetchMachines(scope);
			} finally {
				setFilterOptionsLoaded(true);
			}
		};

		init();
	}, []);

	useEffect(() => {
		const hasFilters =
			!isAllValue(filters.customer) ||
			!isAllValue(filters.site) ||
			!isAllValue(filters.user) ||
			!isAllValue(filters.machine);
		setButtonShow(hasFilters);
	}, [filters.customer, filters.site, filters.user, filters.machine]);

	const appliedDropdownFilters = useMemo(() => {
		const chips = [];
		const customerLabel = customersOptions.find(o => o.value === filters.customer)?.label;
		const siteLabel = sitesOptions.find(o => o.value === filters.site)?.label;
		const userLabel = usersOptions.find(o => o.value === filters.user)?.label;
		const machineLabel = machinesOptions.find(o => o.value === filters.machine)?.label;
		if (customerLabel && !isAllValue(filters.customer)) chips.push({ id: "customer", label: customerLabel });
		if (siteLabel && !isAllValue(filters.site)) chips.push({ id: "site", label: siteLabel });
		if (userLabel && !isAllValue(filters.user)) chips.push({ id: "user", label: userLabel });
		if (machineLabel && !isAllValue(filters.machine)) chips.push({ id: "machine", label: machineLabel });
		return chips;
	}, [
		filters.customer,
		filters.site,
		filters.user,
		filters.machine,
		customersOptions,
		sitesOptions,
		usersOptions,
		machinesOptions,
	]);

	// Helper to generate site options
	const getSiteOptions = (sites) =>
		sites.map((site) => ({
			value: getSiteSelectionValue(site),
			label: `${site.customer_site_name} - ${site.customer_site_number}`,
			searchText: `${site.customer_site_name} ${site.customer_site_number}`.toLowerCase(),
			customer_id: getSiteCustomerId(site),
			site_id: getSiteId(site),
		}));

	const uniqueMachinesByNumber = (list = []) => {
		const seen = new Map();
		list.forEach((mac) => {
			const key =
				normalizeMachineNumber(mac?.machine_number) ||
				normalizeMachineNumber(mac?.machine_name) ||
				String(mac?.machine_id);
			if (!key || seen.has(key)) return;
			seen.set(key, mac);
		});
		return Array.from(seen.values());
	};

	const getMachineOptions = (machines) =>
		uniqueMachinesByNumber(machines).map((mac) => ({
			key: String(mac.machine_id),
			value: mac.machine_id,
			label: `${mac.machine_name} - ${mac.machine_number}`,
			machine_number: mac.machine_number,
			searchText: `${mac.machine_name} ${mac.machine_number}`.toLowerCase(),
		}));

	const isApprovedUser = (row) => {
		const statusValue = row?.user_status ?? row?.status ?? null;
		if (Number(statusValue) === 1) return true;
		const normalized = String(statusValue || "").trim().toLowerCase();
		return normalized === "approved" || normalized === "active";
	};
	const isAdminRow = (row) => {
		const roleToken =
			row?.user_role_name ||
			row?.user_role ||
			row?.role?.role_name ||
			row?.role?.role_type ||
			row?.user_type ||
			"";
		const normalized = String(roleToken).trim().toLowerCase();
		return normalized === "admin" || normalized.includes("admin");
	};
	const getUserAllocatedSiteIds = (userRow) => {
		if (Array.isArray(userRow?.allocations) && userRow.allocations.length > 0) {
			return Array.from(
				new Set(
					userRow.allocations
						.map((a) => Number(a?.site_id))
						.filter((sid) => Number.isFinite(sid) && sid > 0)
				)
			);
		}
		if (Array.isArray(userRow?.user_sites)) {
			return Array.from(
				new Set(
					userRow.user_sites
						.map((sid) => Number(sid))
						.filter((sid) => Number.isFinite(sid) && sid > 0)
				)
			);
		}
		const rawSites = `${userRow?.user_sites || ""}`.trim();
		if (!rawSites) return [];
		return rawSites
			.split(",")
			.map((sid) => Number(sid))
			.filter((sid) => Number.isFinite(sid) && sid > 0);
	};
	const getAccessibleSiteIdsForUser = (userRow, scopedSites) => {
		const allocatedIds = getUserAllocatedSiteIds(userRow);
		if (allocatedIds.length > 0) return allocatedIds;
		if (isAdminRow(userRow)) return scopedSites.map((site) => Number(site.site_id));
		return [];
	};
	const getCustomerOptions = (customers) =>
		customers.map((cus) => ({
			value: cus.cus_id,
			label: `${cus.customer_name} - ${cus.customer_number}`,
			searchText: `${cus.customer_name} ${cus.customer_number}`.toLowerCase(),
		}));
	const getUserOptions = (usersList) =>
		uniqueBy(
			usersList.map((user) => ({
				value: user.user_id,
				label: `${user.user_display_name} (${user.user_email})`,
				displayName: user.user_display_name,
				email: user.user_email,
				searchText: `${user.user_display_name} ${user.user_email}`.toLowerCase(),
				user_email: user.user_email,
			})),
			(opt) => normalizeEmail(opt?.user_email) || String(opt?.value)
		);

	const baseSites = useMemo(() => {
		if (accessScope.allowNone) return [];
		if (accessScope.allowAll) return sites;
		if (allocatedSiteIds.length > 0) {
			return sites.filter((site) => allocatedSiteIds.includes(getSiteId(site)));
		}
		if (allocatedCustomerIds.length > 0) {
			return sites.filter((site) => allocatedCustomerIds.includes(getSiteCustomerId(site)));
		}
		return [];
	}, [accessScope.allowAll, accessScope.allowNone, allocatedCustomerIds, allocatedSiteIds, sites]);

	const areFiltersEqual = (a, b) =>
		a.customer === b.customer &&
		a.site === b.site &&
		a.user === b.user &&
		a.machine === b.machine;

	const computeScopedSites = (draftFilters) => {
		let scopedSites = baseSites;
		if (!isAllValue(draftFilters.site)) {
			const siteId = getFilterSiteId(draftFilters.site);
			const customerId = getFilterSiteCustomerId(draftFilters.site);
			scopedSites = scopedSites.filter(
				(site) =>
					getSiteId(site) === siteId &&
					(!customerId || getSiteCustomerId(site) === customerId)
			);
		}
		if (!isAllValue(draftFilters.customer)) {
			const customerId = Number(draftFilters.customer);
			scopedSites = scopedSites.filter((site) => getSiteCustomerId(site) === customerId);
		}
		if (!isAllValue(draftFilters.user)) {
			const selectedUser = users.find((user) => Number(user.user_id) === Number(draftFilters.user));
			if (!selectedUser || !isApprovedUser(selectedUser)) {
				return [];
			}
			const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
			const allowedSiteIds = new Set(userSiteIds);
			scopedSites = scopedSites.filter((site) => allowedSiteIds.has(Number(site.site_id)));
		}
		return scopedSites;
	};

	const getCustomerOptionsForSites = (scopedSites) => {
		const customerIds = new Set(scopedSites.map(getSiteCustomerId).filter(Boolean));
		const scopedCustomers =
			accessScope.allowAll && scopedSites.length === baseSites.length
				? customers
				: customers.filter((customer) => customerIds.has(Number(customer?.cus_id)));
		return getCustomerOptions(scopedCustomers);
	};

	const getMachineOptionsForSites = (scopedSites) => {
		const siteIds = new Set(scopedSites.map(getSiteId).filter(Boolean));
		const scopedMachines =
			accessScope.allowAll && scopedSites.length === baseSites.length
				? machines
				: machines.filter((machine) => {
					const siteId = toPositiveNumber(machine?.site_id ?? machine?.customer_site_id);
					return siteId && siteIds.has(siteId);
				});
		return getMachineOptions(scopedMachines);
	};

	const getUserOptionsForScope = (scopedSites = baseSites) => {
		const siteIdsForScope = new Set(scopedSites.map(getSiteId).filter(Boolean));
		const scopedUsers =
			accessScope.allowAll && scopedSites.length === baseSites.length
				? users
				: users.filter((userRow) => {
					if (!isApprovedUser(userRow) || isAdminRow(userRow)) return false;
					const userSiteIds = getAccessibleSiteIdsForUser(userRow, baseSites);
					return userSiteIds.some((siteId) => siteIdsForScope.has(Number(siteId)));
				});
		return getUserOptions(scopedUsers);
	};

	const buildOptionsFromScope = (scopedSites = baseSites) => {
		return {
			customerOptions: getCustomerOptionsForSites(scopedSites),
			siteOptions: getSiteOptions(scopedSites),
			machineOptions: getMachineOptionsForSites(scopedSites),
			userOptions: getUserOptionsForScope(scopedSites),
		};
	};

	const validateSelections = (draftFilters, options) => {
		const isValid = (value, list) => isAllValue(value) || list.some((opt) => opt.value === value);
		const nextFilters = { ...draftFilters };
		if (!isValid(nextFilters.customer, options.customerOptions)) nextFilters.customer = ALL_VALUE;
		if (!isValid(nextFilters.site, options.siteOptions)) nextFilters.site = ALL_VALUE;
		if (!isValid(nextFilters.user, options.userOptions)) nextFilters.user = ALL_VALUE;
		if (!isValid(nextFilters.machine, options.machineOptions)) nextFilters.machine = ALL_VALUE;
		return nextFilters;
	};

	const applyAutoSelection = (draftFilters) => {
		const nextFilters = { ...draftFilters };
		if (!isAllValue(nextFilters.site)) {
			const selectedFilterSiteId = getFilterSiteId(nextFilters.site);
			const selectedFilterCustomerId = getFilterSiteCustomerId(nextFilters.site);
			const selectedSite = baseSites.find(
				(site) =>
					getSiteId(site) === selectedFilterSiteId &&
					(!selectedFilterCustomerId || getSiteCustomerId(site) === selectedFilterCustomerId)
			);
			if (!selectedSite) {
				nextFilters.site = ALL_VALUE;
			} else if (isAllValue(nextFilters.customer)) {
				nextFilters.customer = getSiteCustomerId(selectedSite) ?? nextFilters.customer;
			}
		}
		if (!isAllValue(nextFilters.customer) && !isAllValue(nextFilters.site)) {
			const selectedFilterSiteId = getFilterSiteId(nextFilters.site);
			const selectedFilterCustomerId = getFilterSiteCustomerId(nextFilters.site);
			const selectedSite = baseSites.find(
				(site) =>
					getSiteId(site) === selectedFilterSiteId &&
					(!selectedFilterCustomerId || getSiteCustomerId(site) === selectedFilterCustomerId)
			);
			if (selectedSite && getSiteCustomerId(selectedSite) !== Number(nextFilters.customer)) {
				nextFilters.site = ALL_VALUE;
			}
		}

		return nextFilters;
	};

	const computeNextState = (inputFilters) => {
		let draftFilters = { ...inputFilters };
		for (let i = 0; i < 2; i += 1) {
			draftFilters = applyAutoSelection(draftFilters);
			const scopedSites = computeScopedSites(draftFilters);
			const options = buildOptionsFromScope(scopedSites);
			options.userOptions = getUserOptionsForScope(scopedSites, draftFilters);
			const validatedFilters = validateSelections(draftFilters, options);
			if (areFiltersEqual(draftFilters, validatedFilters)) {
				return { filters: validatedFilters, options };
			}
			draftFilters = validatedFilters;
		}
		const scopedSites = computeScopedSites(draftFilters);
		const options = buildOptionsFromScope(scopedSites);
		options.userOptions = getUserOptionsForScope(scopedSites, draftFilters);
		return { filters: draftFilters, options };
	};

	useEffect(() => {
		if (appliedSelectedSiteRef.current) return;
		if (!selectedSite) {
			appliedSelectedSiteRef.current = true;
			setInitialSiteFilterState({ siteNumber: "", siteKey: "", ready: true });
			return;
		}
		if (!filterOptionsLoaded) return;
		const selectedSiteRow =
			(selectedSiteData?.site_id
				? baseSites.find(
						(site) =>
							getSiteId(site) === Number(selectedSiteData.site_id) &&
							(!selectedSiteData?.cus_id ||
								getSiteCustomerId(site) === Number(selectedSiteData.cus_id))
				  )
				: null) ||
			(selectedSiteKey
				? baseSites.find((site) => getSiteSelectionValue(site) === selectedSiteKey)
				: null) ||
			baseSites.find((site) => String(site.customer_site_number) === String(selectedSite));
		appliedSelectedSiteRef.current = true;
		setInitialSiteFilterState({ siteNumber: selectedSite, siteKey: selectedSiteKey || "", ready: true });
		if (!selectedSiteRow) return;
		setFilters((prev) => ({
			...prev,
			customer: getSiteCustomerId(selectedSiteRow) ?? prev.customer,
			site: getSiteSelectionValue(selectedSiteRow),
		}));
	}, [selectedSite, selectedSiteData, selectedSiteKey, baseSites, filterOptionsLoaded]);

	useEffect(() => {
		const { filters: nextFilters, options } = computeNextState(filters);
		setCustomersOptions(withAllOption(options.customerOptions));
		setSitesOptions(withAllOption(options.siteOptions));
		setUsersOptions(withAllOption(options.userOptions));
		setMachinesOptions(withAllOption(options.machineOptions));
		if (!areFiltersEqual(filters, nextFilters)) {
			setFilters(nextFilters);
		}
	}, [
		baseSites,
		customers,
		machines,
		users,
		filters,
		withAllOption,
	]);

	// Handle Customer Change
	const handleCustomerChange = (_event, option) => {
		setButtonShow(true);
		const selectedCustomer = option?.value ?? ALL_VALUE;
		setMachineInput("");
		setFilters((prevFilters) => ({
			...prevFilters,
			customer: selectedCustomer,
			site: ALL_VALUE,
			user: ALL_VALUE,
			machine: ALL_VALUE,
		}));
	};

	// Handle Site Change
	const handleSiteChange = (_event, option) => {
		setButtonShow(true);
		const selectedSiteId = option?.value ?? ALL_VALUE;
		setMachineInput("");
		setFilters((prevFilters) => ({
			...prevFilters,
			site: selectedSiteId,
		}));
	};

	// Handle User Change
	const handleUserChange = (_event, option) => {
		setButtonShow(true);
		const selectedUserId = option?.value ?? ALL_VALUE;
		setFilters((prevFilters) => ({ ...prevFilters, user: selectedUserId }));
	};

	const handleMachineChange = (_event, option) => {
		setButtonShow(true);
		const selectedMachineId = option?.value ?? ALL_VALUE;
		setFilters((prevFilters) => ({
			...prevFilters,
			machine: selectedMachineId,
		}));
	};

	const handleClearFilters = () => {
		setFilters({
			customer: ALL_VALUE,
			site: ALL_VALUE,
			user: ALL_VALUE,
			region: t('MENU_ITEMS.ALL'),
			machine: ALL_VALUE,
		});

		setCustomerInput("");
		setSiteInput("");
		setUserInput("");
		setMachineInput("");
		setButtonShow(false);
	};

	const handleClearDropdownFilter = (filterId) => {
		setFilters((prevFilters) => ({
			...prevFilters,
			[filterId]: ALL_VALUE,
			...(filterId === "customer" ? { site: ALL_VALUE, user: ALL_VALUE, machine: ALL_VALUE } : {}),
		}));

		if (filterId === "customer") {
			setCustomerInput("");
			setSiteInput("");
			setUserInput("");
			setMachineInput("");
		}
		if (filterId === "site") setSiteInput("");
		if (filterId === "user") setUserInput("");
		if (filterId === "machine") setMachineInput("");
	};

const normalizeFilterValue = (value) => (isAllValue(value) ? t('MENU_ITEMS.ALL') : value);
	const buildChartQuery = () => {
		const siteId = isAllValue(filters.site) ? null : getFilterSiteId(filters.site);
		const siteCustomerId = isAllValue(filters.site) ? null : getFilterSiteCustomerId(filters.site);
		const selectedSiteRow = siteId
			? sites.find(
					(site) =>
						getSiteId(site) === siteId &&
						(!siteCustomerId || getSiteCustomerId(site) === siteCustomerId)
			  )
			: null;
		const siteNumber =
			selectedSiteRow?.customer_site_number ||
			selectedSiteRow?.site_number ||
			null;
		const [rangeStart, rangeEnd] = Array.isArray(selectedDateRange) ? selectedDateRange : [];
		const disabledChartFilterIds = new Set(["top_parts", "top_customers", "top_sites"]);
		return {
			startDate: formatApiDate(rangeStart) || selectedRange.startDate,
			endDate: formatApiDate(rangeEnd) || selectedRange.endDate,
			mode: selectedRange.key,
			user: normalizeFilterValue(filters.user),
			site: siteId || t('MENU_ITEMS.ALL'),
			customer: normalizeFilterValue(filters.customer),
			machine: normalizeFilterValue(filters.machine),
			site_number: siteId ? siteNumber : null,
			site_id: siteId || null,
			cus_id: siteCustomerId || (siteId ? getSiteCustomerId(selectedSiteRow) : null),
			additionalFilters: (additionalFilters || []).filter(
				(filter) => !disabledChartFilterIds.has(filter?.id)
			),
		};
	};
	const isInitialSiteFilterPending =
		Boolean(selectedSite) &&
		(!initialSiteFilterState.ready ||
			initialSiteFilterState.siteNumber !== selectedSite ||
			initialSiteFilterState.siteKey !== (selectedSiteKey || ""));
	// Handler for button clicks
	const handleButtonClick = (label, key) => {
		const currentDate = new Date();
		let startDate, endDate;

		switch (key) {
			case "T": // Today
				startDate = new Date(currentDate);
				startDate.setHours(0, 0, 0, 0);

				endDate = new Date(currentDate);
				endDate.setHours(23, 59, 59, 999);
				break;
			case "W": // Week
				startDate = getStartOfWeek(new Date(currentDate));
				endDate = getEndOfWeek(new Date(currentDate));
				break;
			case "Q": // Quarter
				startDate = getStartOfQuarter(new Date(currentDate));
				endDate = getEndOfQuarter(new Date(currentDate));
				break;
			case "M": // Month
				startDate = getStartOfMonth(new Date(currentDate));
				endDate = getEndOfMonth(new Date(currentDate));
				break;
			case "Y": // Year
				startDate = getStartOfYear(new Date(currentDate));
				endDate = getEndOfYear(new Date(currentDate));
				break;

			case t('MENU_ITEMS.ALL'): // Year
				const startOfTwoYearsAgo = new Date(currentDate);
				startOfTwoYearsAgo.setFullYear(currentDate.getFullYear() - 2); // Set start date to 2 years ago
				startDate = startOfTwoYearsAgo;
				endDate = new Date(currentDate);
				break;
			default: // Custom
				startDate = null;
				endDate = null;
				break;
		}

		setSelectedRange({
			label,
			startDate: startDate ? formatDate(startDate) : "",
			endDate: endDate ? formatDate(endDate) : "",
			key,
		});
		setSelectedDateRange([startDate, endDate]);
	};

	const chartQueryRef = useRef("");

	useEffect(() => {
		if (isInitialSiteFilterPending) return;
		const data = buildChartQuery();
		const key = JSON.stringify(data);
		if (key === chartQueryRef.current) return;
		chartQueryRef.current = key;
		setLoading(true);
		setChartQueryFilter(data);
	}, [selectedRange, selectedDateRange, filters, additionalFilters, sites, isInitialSiteFilterPending]);

	const canSeeAnalytics =
		canAccessOrderAnalytics(hasPermission, user, enabledAdminModuleSet);
	if (!canSeeAnalytics) {
		return <NotAuthorized />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				maxWidth: "100vw",
				display: "flex",
				flexDirection: "column",
				alignItems: "stretch",
				py: 3,
				px: { xs: 2, md: 5 },
				overflowX: "hidden",
			}}>
			<PageTitle title={t("ITEMS_TEXT.ORDER_ANALYTICS", { defaultValue: "Quote Analytics" })} />
			<Paper
				elevation={0}
				sx={{
					px: { xs: 0, md: 0 },
					py: { xs: 1, md: 1.5 },
					mb: 2,
					border: 0,
					borderRadius: 0,
					background: "inherit",
					boxShadow: "none",
					position: { xs: "static", md: "sticky" },
					top: { md: 75 },
					zIndex: 1051,
				}}>
				<Stack
					direction={{ xs: "column", lg: "row" }}
					alignItems={{ xs: "stretch", lg: "center" }}
					justifyContent="space-between"
					spacing={1.5}
					useFlexGap
					flexWrap="wrap"
				>
					<Stack
						data-tour="analytics-filters"
						direction={{ xs: "column", sm: "row" }}
						alignItems="stretch"
						spacing={1}
						useFlexGap
						flexWrap="wrap"
						sx={{ flex: "1 1 620px", minWidth: 0, width: "100%" }}
					>
						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							options={displayedCustomerOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							onInputChange={(_, val) => setCustomerInput(val)}
							inputValue={customerInput}
							value={customersOptions.find(o => o.value === filters.customer) || null}
							onChange={handleCustomerChange}
							sx={{
								flex: { xs: "1 1 100%", sm: "1 1 210px" },
								minWidth: { xs: "100%", sm: 210 },
								maxWidth: { xs: "100%", lg: 260 },
								"& .MuiOutlinedInput-root": { height: 40, bgcolor: "background.paper" },
								"& .MuiInputBase-input": { fontSize: 14, py: 0.25 },
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label={t('PLACEHOLDER.FILTER_BY_CUSTOMER')}
									placeholder={t('PLACEHOLDER.FILTER_BY_CUSTOMER')}
									variant="outlined"
								/>
							)}
						/>

						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							disabled={false}
							options={displayedSiteOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							onInputChange={(_, val) => setSiteInput(val)}
							inputValue={siteInput}
							value={sitesOptions.find(o => o.value === filters.site) || null}
							onChange={handleSiteChange}
							sx={{
								flex: { xs: "1 1 100%", sm: "1 1 210px" },
								minWidth: { xs: "100%", sm: 210 },
								maxWidth: { xs: "100%", lg: 260 },
								"& .MuiOutlinedInput-root": { height: 40, bgcolor: "background.paper" },
								"& .MuiInputBase-input": { fontSize: 14, py: 0.25 },
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label={t('PLACEHOLDER.FILTER_BY_SITE')}
									placeholder={t('PLACEHOLDER.FILTER_BY_SITE')}
									variant="outlined"
								/>
							)}
						/>

						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							disabled={false}
							options={displayedUserOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							renderOption={(props, option) => (
								<li {...props}>
									<Tooltip title={option?.label || ""} placement="right">
										<Box sx={{ minWidth: 0 }}>
											<Typography
												variant="body1"
												noWrap
												sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
												{option?.displayName || option?.label || ""}
											</Typography>
											{option?.email && (
												<Typography
													variant="body2"
													color="text.secondary"
													noWrap
													sx={{ fontSize: "11px", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
													{option.email}
												</Typography>
											)}
										</Box>
									</Tooltip>
								</li>
							)}
							onInputChange={(_, val) => setUserInput(val)}
							inputValue={userInput}
							value={usersOptions.find(o => o.value === filters.user) || null}
							onChange={handleUserChange}
							sx={{
								flex: { xs: "1 1 100%", sm: "1 1 210px" },
								minWidth: { xs: "100%", sm: 210 },
								maxWidth: { xs: "100%", lg: 260 },
								"& .MuiOutlinedInput-root": { height: 40, bgcolor: "background.paper" },
								"& .MuiInputBase-input": { fontSize: 14, py: 0.25 },
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label={t('PLACEHOLDER.FILTER_BY_USER')}
									placeholder={t('PLACEHOLDER.FILTER_BY_USER')}
									variant="outlined"
								/>
							)}
						/>
						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							disabled={false}
							options={displayedMachineOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							onInputChange={(_, val) => setMachineInput(val)}
							inputValue={machineInput}
							value={machinesOptions.find(o => o.value === filters.machine) || null}
							onChange={handleMachineChange}
							sx={{
								flex: { xs: "1 1 100%", sm: "1 1 210px" },
								minWidth: { xs: "100%", sm: 210 },
								maxWidth: { xs: "100%", lg: 260 },
								"& .MuiOutlinedInput-root": { height: 40, bgcolor: "background.paper" },
								"& .MuiInputBase-input": { fontSize: 14, py: 0.25 },
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label={t('PLACEHOLDER.FILTER_BY_MACHINE')}
									placeholder={t('PLACEHOLDER.FILTER_BY_MACHINE')}
									variant="outlined"
									size="small"
								/>
							)}
						/>
						{buttonShow && (
							<Button
								variant="outlined"
								size="small"
								endIcon={<FilterAltOffIcon />}
								onClick={handleClearFilters}
								sx={{
									height: 40,
									minWidth: { xs: "100%", sm: 108 },
									alignSelf: { xs: "stretch", sm: "center" },
									textTransform: "none",
									fontWeight: 700,
								}}>
								{t('BUTTON.CLEAR')}
							</Button>
						)}
					</Stack>
					<Stack
						data-tour="analytics-date-range"
						direction={{ xs: "column", sm: "row" }}
						alignItems="center"
						justifyContent={{ xs: "flex-start", lg: "flex-end" }}
						spacing={0.9}
						useFlexGap
						flexWrap="wrap"
						sx={{ flex: "0 0 auto", width: { xs: "100%", lg: "auto" }, ml: { lg: "auto" } }}
					>
						<Stack direction="row" alignItems="center" spacing={0.75}>
							{buttons.map((button) => (
								<Button
									key={button.key}
									onClick={() => handleButtonClick(button.label, button.key)}
									variant="roundedGreyBgSquareContained"
									sx={{
										background: selectedRange.key === button.key ? (theme) => theme.palette.primary.main : (theme) => alpha(theme.palette.primary.light, 0.2),
										color: selectedRange.key === button.key ? "#fff" : (theme) => theme.palette.primary.main
									}}>
									{button.key}
								</Button>
							))}
						</Stack>
						<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
						<Box sx={{ width: { xs: "100%", sm: 300 } }}>
							<DateRange
								setSelectedRange={setSelectedRange}
								selectedRange={selectedRange}
								selectedDateRange={selectedDateRange}
								setSelectedDateRange={setSelectedDateRange}
							/>
						</Box>
					</Stack>
				</Stack>
				<Stack
					direction={{ xs: "column", md: "row" }}
					alignItems={{ xs: "flex-start", md: "center" }}
					justifyContent="space-between"
					spacing={2}
					sx={{ mt: 2.5 }}
				>
					<Box sx={{ minWidth: 180 }}>
						<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{selectedRange.label}</Typography>
						<Typography variant="body1" sx={{ color: "#111827" }}>
							{selectedRange.startDate} - {selectedRange.endDate}
						</Typography>
					</Box>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent={{ xs: "flex-start", md: "flex-end" }}
						spacing={1}
						useFlexGap
						flexWrap="wrap"
						sx={{ minHeight: 32, flex: "1 1 auto" }}
					>
						{(additionalFilters.length > 0 || appliedDropdownFilters.length > 0) && (
							<>
								<Stack direction="row" alignItems="center" spacing={0.5}>
									<FilterAltIcon color="primary" />
									<Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
										{t('UI_TEXT.APPLIED_FILTERS')} :{" "}
									</Typography>
								</Stack>
								{appliedDropdownFilters.map((filter) => (
									<Chip
										key={`dropdown-${filter.id}`}
										size="small"
										label={filter.label}
										onDelete={() => handleClearDropdownFilter(filter.id)}
										sx={{
											height: 32,
											borderRadius: 1,
											bgcolor: "#eeeeee",
											color: "#111827",
											fontWeight: 600,
											"& .MuiChip-label": {
												maxWidth: { xs: 220, md: 420 },
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										}}
									/>
								))}
								{/* {additionalFilters.map((filter) => (
									<Chip
										key={filter.id}
										size="small"
										label={`${filter.chartName || t("UI_TEXT.FILTERED_BY")} - ${filter.name || filter.value || ""}`}
										onDelete={() =>
											setAdditionalFilters(additionalFilters.filter((f) => f.id !== filter.id))
										}
										sx={{
											height: 32,
											borderRadius: 1,
											bgcolor: "#eeeeee",
											color: "#111827",
											fontWeight: 600,
											"& .MuiChip-label": {
												maxWidth: { xs: 220, md: 420 },
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										}}
									/>
								))} */}
							</>
						)}
					</Stack>
				</Stack>
				<Box sx={{ mt: 1.5, height: 4, borderRadius: 999, overflow: "hidden" }}>
					{loading ? <LinearProgress sx={{ height: 4 }} /> : null}
				</Box>
			</Paper>

			{showCharts ? (
				<AnalyticsCharts
					loading={loading}
					chartFilters={chartQueryFilter}
					setLoading={setLoading}
					selectedRange={selectedRange}
					additionalFilters={additionalFilters}
					setAdditionalFilters={setAdditionalFilters}
					setShowCharts={setShowCharts}
					setSelectedMetric={setSelectedMetric}
					selectedMetric={selectedMetric}
				/>
			) : (
				<AnalyticsTables
					loading={loading}
					chartFilters={chartQueryFilter}
					setLoading={setLoading}
					selectedRange={selectedRange}
					additionalFilters={additionalFilters}
					setAdditionalFilters={setAdditionalFilters}
					setShowCharts={setShowCharts}
					setSelectedMetric={setSelectedMetric}
					selectedMetric={selectedMetric}
				/>
			)}
			{/* <FilterBox additionalFilters={additionalFilters} setAdditionalFilters={setAdditionalFilters} /> */}
		</Box>
	);
};

export default Analytics;
